import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Space, Spin, Alert, Table } from 'antd';
import { DownloadOutlined, FileWordOutlined, FileExcelOutlined } from '@ant-design/icons';
import mammoth from 'mammoth';
import * as XLSX from 'xlsx';

/**
 * OfficeDocumentViewer - Client-side viewer for Office documents
 *
 * Supports:
 * - DOCX: Renders as HTML using mammoth.js
 * - XLSX: Renders as interactive table using SheetJS
 * - PPTX: Shows structured download option (client-side PPTX rendering is complex)
 *
 * Works with local URLs (localhost) unlike external viewers.
 */
const OfficeDocumentViewer = ({
  fileUrl,
  filename,
  fileType,
  onDownload,
  containerStyle = {}
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [content, setContent] = useState(null);
  const [sheetData, setSheetData] = useState(null);
  const [activeSheet, setActiveSheet] = useState(0);

  useEffect(() => {
    if (!fileUrl) return;

    const loadDocument = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(fileUrl, {
          headers: {
            'Authorization': `Bearer ${JSON.parse(localStorage.getItem('user'))?.access_token}`
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to load file: ${response.statusText}`);
        }

        const arrayBuffer = await response.arrayBuffer();

        if (fileType === 'docx') {
          // Render DOCX as HTML
          const result = await mammoth.convertToHtml({ arrayBuffer });
          setContent(result.value);
          if (result.messages.length > 0) {
            console.warn('Mammoth conversion messages:', result.messages);
          }
        } else if (fileType === 'xlsx' || fileType === 'xls') {
          // Render XLSX as table
          const workbook = XLSX.read(arrayBuffer, { type: 'array' });
          const sheets = workbook.SheetNames.map((name, index) => ({
            name,
            data: XLSX.utils.sheet_to_json(workbook.Sheets[name], { header: 1 })
          }));
          setSheetData(sheets);
          setActiveSheet(0);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error loading document:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    loadDocument();
  }, [fileUrl, fileType]);

  const handleDownloadClick = () => {
    if (onDownload) {
      onDownload();
    } else {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Render DOCX content
  if (fileType === 'docx') {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', ...containerStyle }}>
        {/* Toolbar */}
        <div
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid #e8e8e8',
            backgroundColor: 'var(--rlc-surface-subtle)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Space>
            <FileWordOutlined style={{ fontSize: 20, color: '#2b579a' }} />
            <span style={{ fontWeight: 500 }}>{filename}</span>
          </Space>
          <Button icon={<DownloadOutlined />} onClick={handleDownloadClick}>
            Download
          </Button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px', backgroundColor: 'var(--rlc-surface)' }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" tip="Loading document..." />
            </div>
          )}

          {error && (
            <Alert
              message="Failed to Load Document"
              description={error}
              type="error"
              showIcon
              action={
                <Button onClick={handleDownloadClick} icon={<DownloadOutlined />}>
                  Download
                </Button>
              }
            />
          )}

          {content && (
            <div
              style={{
                maxWidth: '800px',
                margin: '0 auto',
                backgroundColor: 'white',
                padding: '40px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                fontFamily: 'Georgia, serif',
                fontSize: '14px',
                lineHeight: '1.6'
              }}
              dangerouslySetInnerHTML={{ __html: content }}
            />
          )}
        </div>
      </div>
    );
  }

  // Render XLSX content
  if (fileType === 'xlsx' || fileType === 'xls') {
    const currentSheet = sheetData?.[activeSheet];

    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', ...containerStyle }}>
        {/* Toolbar */}
        <div
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid #e8e8e8',
            backgroundColor: 'var(--rlc-surface-subtle)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '8px'
          }}
        >
          <Space>
            <FileExcelOutlined style={{ fontSize: 20, color: 'var(--rlc-state-success)' }} />
            <span style={{ fontWeight: 500 }}>{filename}</span>
          </Space>

          {sheetData && sheetData.length > 1 && (
            <Space>
              <span style={{ fontSize: 12, color: 'var(--rlc-text-secondary)' }}>Sheet:</span>
              {sheetData.map((sheet, index) => (
                <Button
                  key={index}
                  size="small"
                  type={activeSheet === index ? 'primary' : 'default'}
                  onClick={() => setActiveSheet(index)}
                >
                  {sheet.name}
                </Button>
              ))}
            </Space>
          )}

          <Button icon={<DownloadOutlined />} onClick={handleDownloadClick}>
            Download
          </Button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px', backgroundColor: 'var(--rlc-surface-sunken)' }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" tip="Loading spreadsheet..." />
            </div>
          )}

          {error && (
            <Alert
              message="Failed to Load Spreadsheet"
              description={error}
              type="error"
              showIcon
              action={
                <Button onClick={handleDownloadClick} icon={<DownloadOutlined />}>
                  Download
                </Button>
              }
            />
          )}

          {currentSheet && currentSheet.data.length > 0 && (
            <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '4px' }}>
              <Table
                dataSource={currentSheet.data.slice(1).map((row, index) => ({
                  key: index,
                  ...row.reduce((acc, cell, cellIndex) => {
                    acc[`col${cellIndex}`] = cell;
                    return acc;
                  }, {})
                }))}
                columns={
                  currentSheet.data[0]?.map((header, index) => ({
                    title: header || `Column ${index + 1}`,
                    dataIndex: `col${index}`,
                    key: `col${index}`,
                    ellipsis: true,
                    width: 150
                  })) || []
                }
                pagination={{
                  pageSize: 50,
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} rows`
                }}
                size="small"
                scroll={{ x: 'max-content' }}
                bordered
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Fallback for unsupported types
  return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <Alert
        message="Preview Not Available"
        description="This file type cannot be previewed. Please download to view."
        type="info"
        showIcon
        action={
          <Button type="primary" onClick={handleDownloadClick} icon={<DownloadOutlined />}>
            Download
          </Button>
        }
      />
    </div>
  );
};

OfficeDocumentViewer.propTypes = {
  fileUrl: PropTypes.string.isRequired,
  filename: PropTypes.string.isRequired,
  fileType: PropTypes.string.isRequired,
  onDownload: PropTypes.func,
  containerStyle: PropTypes.object
};

export default OfficeDocumentViewer;
